import { DateHelper } from '@helpers/date.helper';
import { AccountingPeriod } from '@models/accounting-period';
import { Company } from '@models/company';
import { Contributor, ContributorJson } from '@models/contributor';

import { FormalityBase, FormalityBaseJson } from './formality-base';
import { FormalitySpecificDates } from './formality-specific-dates';
import { LegalFormalityStatus } from './legal-formality-status';
import { LegalMoralPerson, LegalMoralPersonJson } from './legal-moral-person';
import { Convention, ConventionJson } from '../../accounts-approval/accounts-approval-core/models/convention';
import { Dividend, DividendJson } from '../../accounts-approval/accounts-approval-core/models/dividend';
import {
  GeneralAssembly,
  GeneralAssemblyJson
} from '../../accounts-approval/accounts-approval-core/models/general-assembly';
import { Remuneration, RemunerationJson } from '../../accounts-approval/accounts-approval-core/models/remuneration';
import { Result } from '../../accounts-approval/accounts-approval-core/models/result';
import { Scope, ScopeJson } from '../../accounts-approval/accounts-approval-core/models/scope';
import {
  CreationPhysicalPerson,
  CreationPhysicalPersonJson
} from '../../creation/creation-core/models/creation-physical-person';
import { FormalitySupervisorEnum } from '../enums/formality-supervisor.enum';

export interface AccountsApprovalJson extends FormalityBaseJson {
  beneficiaries?: Partial<CreationPhysicalPersonJson>[];
  validated_at?: string;
  refreshed_at?: string;
  scope?: ScopeJson;
  general_assembly?: GeneralAssemblyJson;
  result?: Result;
  convention?: ConventionJson;
  remunerations?: RemunerationJson[];
  dividends?: DividendJson[];
  business_unit_id?: number;
  moral_beneficiaries?: Partial<LegalMoralPersonJson>[];
  last_reopen_date?: string;
  checked_by?: Partial<ContributorJson>;
  way_of_control?: string;
}

export class AccountsApproval extends FormalityBase {
  constructor(
    public id?: number,
    public formalityType?: string,
    public codeName?: string,
    public status?: string,
    public formalitySupervisor?: FormalitySupervisorEnum,
    public deliveryDate?: Date,
    public statuses?: LegalFormalityStatus[],
    public businessUnitId?: number,
    public production?: Contributor,
    public note?: string,
    public accountingPeriod?: AccountingPeriod,
    public company?: Company,
    public specificDates?: FormalitySpecificDates,

    public beneficiaries?: CreationPhysicalPerson[],
    public validatedAt?: Date,
    public refreshedAt?: Date,
    public scope?: Scope,
    public generalAssembly?: GeneralAssembly,
    public result?: Result,
    public convention?: Convention,
    public remunerations?: Remuneration[],
    public dividends?: Dividend[],
    public moralBeneficiaries?: LegalMoralPerson[],
    public lastReopenDate?: Date,
    public checkedBy?: Contributor,
    public wayOfControl?: string
  ) {
    super(
      id,
      formalityType,
      codeName,
      status,
      formalitySupervisor,
      deliveryDate,
      statuses,
      businessUnitId,
      production,
      note,
      accountingPeriod,
      company,
      specificDates
    );
  }

  static toWayOfControlAccountsApprovalJson(
    accountsApproval: Partial<AccountsApproval>
  ): Partial<AccountsApprovalJson> {
    return {
      id: accountsApproval.id,
      way_of_control: accountsApproval.wayOfControl
    };
  }

  static fromJson(json: Partial<AccountsApprovalJson>): AccountsApproval {
    let accountsApproval = new AccountsApproval();

    accountsApproval = { ...accountsApproval, ...FormalityBase.fromJson(json) };

    accountsApproval.beneficiaries = json.beneficiaries
      ? json.beneficiaries.map((beneficiaryJson: CreationPhysicalPersonJson) =>
          CreationPhysicalPerson.fromJson(beneficiaryJson)
        )
      : [];
    accountsApproval.validatedAt = json.validated_at ? DateHelper.fromUtc(json.validated_at) : null;
    accountsApproval.refreshedAt = json.refreshed_at ? DateHelper.fromUtc(json.refreshed_at) : null;
    accountsApproval.scope = json.scope ? Scope.fromJson(json.scope) : null;
    accountsApproval.generalAssembly = json.general_assembly ? GeneralAssembly.fromJson(json.general_assembly) : null;
    accountsApproval.result = json.result ? Result.fromJson(json.result) : null;
    accountsApproval.convention = json.convention ? Convention.fromJson(json.convention) : null;
    accountsApproval.remunerations = json.remunerations
      ? json.remunerations.map((remunerationJson: RemunerationJson) => Remuneration.fromJson(remunerationJson))
      : null;
    accountsApproval.dividends = json.dividends
      ? json.dividends.map((dividendJson: DividendJson) => Dividend.fromJson(dividendJson))
      : null;
    accountsApproval.moralBeneficiaries = json.moral_beneficiaries
      ? json.moral_beneficiaries.map((moralBeneficiaryJson: LegalMoralPersonJson) =>
          LegalMoralPerson.fromJson(moralBeneficiaryJson)
        )
      : [];
    accountsApproval.lastReopenDate = json.last_reopen_date ? DateHelper.fromUtc(json.last_reopen_date) : null;
    accountsApproval.checkedBy = json.checked_by ? Contributor.fromJson(json.checked_by) : null;

    return accountsApproval;
  }

  static toJson(accountsApproval: AccountsApproval): Partial<AccountsApprovalJson> {
    return {
      ...FormalityBase.toJson(accountsApproval),

      accounting_period: accountsApproval.accountingPeriod?.id
        ? AccountingPeriod.toJson(accountsApproval.accountingPeriod)
        : null,
      beneficiaries: accountsApproval.beneficiaries
        ? accountsApproval.beneficiaries.map((beneficiary: CreationPhysicalPerson) =>
            CreationPhysicalPerson.toJson(beneficiary)
          )
        : [],
      validated_at: accountsApproval.validatedAt
        ? DateHelper.format(accountsApproval.validatedAt, 'YYYY-MM-DD HH:mm:ss')
        : null,
      refreshed_at: accountsApproval.refreshedAt
        ? DateHelper.format(accountsApproval.refreshedAt, 'YYYY-MM-DD HH:mm:ss')
        : null,
      scope: accountsApproval.scope ? Scope.toJson(accountsApproval.scope) : null,
      general_assembly: accountsApproval.generalAssembly
        ? GeneralAssembly.toJson(accountsApproval.generalAssembly)
        : null,
      result: accountsApproval.result ? Result.toJson(accountsApproval.result) : null,
      convention: accountsApproval.convention ? Convention.toJson(accountsApproval.convention) : null,
      remunerations: accountsApproval.remunerations
        ? accountsApproval.remunerations.map((remuneration: Remuneration) => Remuneration.toJson(remuneration))
        : null,
      dividends: accountsApproval.dividends
        ? accountsApproval.dividends.map((dividend: Dividend) => Dividend.toJson(dividend))
        : null,
      moral_beneficiaries: accountsApproval.moralBeneficiaries
        ? accountsApproval.moralBeneficiaries.map((moralBeneficiary: LegalMoralPerson) =>
            LegalMoralPerson.toJson(moralBeneficiary)
          )
        : [],
      last_reopen_date: accountsApproval.lastReopenDate
        ? DateHelper.format(accountsApproval.lastReopenDate, 'YYYY-MM-DD HH:mm:ss')
        : null,
      checked_by: accountsApproval.checkedBy ? Contributor.toJson(accountsApproval.checkedBy) : null
    };
  }

  static toCheckedByAccountsApprovalJson(accountsApproval: Partial<AccountsApproval>): Partial<AccountsApprovalJson> {
    return {
      id: accountsApproval.id,
      checked_by: accountsApproval.checkedBy ? Contributor.toJson(accountsApproval.checkedBy) : null
    };
  }
}
